import { ConfiguratorDI, Enumerable, Kbo } from "@models/configurators/configurator-di";
import { Container } from "@models/configurators/container";
import { CToken } from "@models/configurators/ctoken";
import { Field } from "@models/configurators/field";
import { Group } from "@models/configurators/group";
import { IDummyNode } from "@models/configurators/kb-object";
import { KbObjectManager } from "@models/configurators/kb-object-manager";
import { RuleContainer } from "@models/configurators/rule-container";
import { UiObject } from "@models/configurators/ui-object";
import { ILayoutConfig } from "@models/entity-models";
import { eRuleType } from "@models/enums";

@Kbo(CToken.LayoutConfig)
export class LayoutConfig extends Container implements ILayoutConfig {

    constructor(kom: KbObjectManager, obj?: any) {
        super(kom);

        kom.recurse(obj, this);
        this.$manager.addKbObject(this, undefined);
        this.$layoutSettingsDirty = false; // reset fields dirty after the recurse

        this.$rulesNode = { id: this.id + "_rules", $type: "RulesNode", allowedChildren: () => [CToken.Rule], getParent: () => this };     
        this.$settingsNode = { id: this.id + "_settings", $type: "SettingsNode", allowedChildren: () => [], getParent: () => this };
    }
               
    // dummy nodes
    public $rulesNode: IDummyNode; // dummy object to act as the data item for the tree node
    public $settingsNode: IDummyNode;
    /**
     * whether the layout settings in this layout have been modified since the last rule cycle
     */
    public $layoutSettingsDirty: boolean;
    public autoValidation: boolean;
    public useMobileDropdowns: boolean;

    private _ruleContainers: RuleContainer[];
    @Enumerable()
    public get ruleContainers(): RuleContainer[] {
        if (!this._ruleContainers) this._ruleContainers = [];
        return this._ruleContainers;
    }
    public set ruleContainers(val) {
        this._ruleContainers = val;
    }

    private _settings: UiObject[];
    @Enumerable()
    public get settings(): UiObject[] {
        if (!this._settings) this._settings = [];
        return this._settings;
    }
    public set settings(val) {
        this._settings = val;
    }

    public filterChildren<T extends UiObject>(tokens: CToken[], allChildren: UiObject[]): T[] {
        let list: T[] = [];
        for (let c of allChildren) {
            if (!tokens.length || tokens.some(t => ConfiguratorDI.instanceOf<T>(c, t))) {
                list.push(c as any);
            }
            c.getChildrenOfType<T>(...tokens).forEach(c2 => list.push(c2));
        }
        return list;
    }

    /**
     * 
     * @param token Gets all the UI children of the given types.
     * @param allChildren 
     */
    public getChildrenOfType<T extends UiObject>(...tokens: CToken[]): T[] {
        return this.filterChildren<T>(tokens,
            this.children
        );
    }
      
    public hasRule(ruleType: string): boolean {
        let rc = this.ruleContainers.find(rc => {
            return rc.ruleType.isEqual(ruleType);
        });
        return (rc != null && rc.js && rc.js.length > 0);
    }
    /**
     * gets a concatinated javascript rule for the specified rule type
     */
    public getRuleJs(ruleType: string): string {
        let rc = this.ruleContainers.find(rc => {
            return rc.ruleType.isEqual(ruleType);
        });
        return rc ? this.getRuleJsFromContainer(rc) : "";
    }

    public getRuleJsFromContainer(ruleContainer: RuleContainer): string {
        // global rule should always be included
        let globalRc = this.ruleContainers.find(rc => rc.ruleType.isEqual(eRuleType.global));
        let globalJs = globalRc ? globalRc.js + "\n\n" : "";
        return globalJs + ruleContainer.js;
    }        

    public addSettingsGroup(): Group {
        let g = new Group(this.$manager);
        this.$manager.addKbObject(g, this);
        this.settings.push(g);
        return g;
    }

    public removeSettingsGroup(g: Group) {
        this.$manager.removeKbObject(g);
        this.settings.remove(g);
    }

    public addSettingsField(group: Group): Field {
        let f = new Field(this.$manager);
        this.$manager.addKbObject(f, group);
        group.children.push(f);
        return f;
    }

    public removeSettingsField(f: Field) {
        this.$manager.removeKbObject(f);
        (f.$parent as Group).children.remove(f);
    }

    public removeRuleContainer(rc: RuleContainer) {
        this.$manager.removeKbObject(rc);
        this.ruleContainers.remove(rc);
    }

    /**
     * returns a flat array of the layout children that are 
     * content containers meant for consumers to fill in with content
     */
    public getContentContainers(): UiObject[]{
        let children = this.getChildrenOfType(
            CToken.Container,
            CToken.PageControl,
            CToken.OverlayPopupControl
        );

        return this.getChildrenOfType(
            CToken.Container,
            CToken.PageControl,
            CToken.OverlayPopupControl
        ).filter(c => !ConfiguratorDI.instanceOf<Container>(c, CToken.Container) || c.isPlaceholder);
    }
}
